import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Presentation, Target, Calendar, Building, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { volunteerOpportunities } from '../data/mock';

const iconMap = {
  Users,
  Presentation,
  Target,
  Calendar,
};

export default function GetInvolvedPage() {
  const [volunteerForm, setVolunteerForm] = useState({ name: '', email: '', phone: '', interest: '', message: '' });
  const [partnerForm, setPartnerForm] = useState({ company: '', contact: '', email: '', phone: '', message: '' });
  const [volunteerSubmitted, setVolunteerSubmitted] = useState(false);
  const [partnerSubmitted, setPartnerSubmitted] = useState(false);
  const [volunteerSubmitting, setVolunteerSubmitting] = useState(false);
  const [partnerSubmitting, setPartnerSubmitting] = useState(false);
  const [volunteerError, setVolunteerError] = useState('');
  const [partnerError, setPartnerError] = useState('');

  // Load Stripe Buy Button script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/buy-button.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    setVolunteerSubmitting(true);
    setVolunteerError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms/volunteer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(volunteerForm),
      });
      if (!res.ok) throw new Error('Failed to submit');
      setVolunteerSubmitted(true);
      setVolunteerForm({ name: '', email: '', phone: '', interest: '', message: '' });
      setTimeout(() => setVolunteerSubmitted(false), 5000);
    } catch (err) {
      setVolunteerError('Something went wrong. Please try again.');
    } finally {
      setVolunteerSubmitting(false);
    }
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setPartnerSubmitting(true);
    setPartnerError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms/partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(partnerForm),
      });
      if (!res.ok) throw new Error('Failed to submit');
      setPartnerSubmitted(true);
      setPartnerForm({ company: '', contact: '', email: '', phone: '', message: '' });
      setTimeout(() => setPartnerSubmitted(false), 5000);
    } catch (err) {
      setPartnerError('Something went wrong. Please try again.');
    } finally {
      setPartnerSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-50 via-white to-teal-50">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-teal-100 rounded-full blur-3xl opacity-40" />
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-amber-100 rounded-full blur-3xl opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-teal-600 font-semibold text-sm tracking-wide uppercase mb-4 block">Get Involved</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">Make a Difference Today</h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Whether you volunteer your time, donate resources, or partner with us, your support helps transform lives and strengthen communities.
            </p>
            
            {/* Stripe Donation Button */}
            <div className="mt-10 flex justify-center" data-testid="stripe-donate-button">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100 max-w-md w-full">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Support Our Mission</h3>
                  <p className="text-slate-600 text-sm">Your generosity helps transform lives and strengthen communities</p>
                </div>
                <div className="flex justify-center">
                  <stripe-buy-button
                    buy-button-id="buy_btn_1THagUCWmRqdmryRlljb5Pe8"
                    publishable-key="pk_live_51THWf2CWmRqdmryRydec8TcivdJXGuzrnImUqQBI7znYhBtGHJLTmRm8gDSNZbL45xhutf47oRlbnL3tYvmAzoIL00d6vXyZGH"
                  >
                  </stripe-buy-button>
                </div>
                <p className="text-center text-xs text-slate-500 mt-6">
                  Donations are tax-deductible. You will receive a receipt for your records.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Volunteer Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-amber-50/40 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-teal-600 font-semibold text-sm tracking-wide uppercase">Volunteer</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">Share Your Time & Talents</h2>
            <p className="text-slate-600">Make a direct impact by volunteering with our programs. We have opportunities for professionals at all levels.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Opportunities */}
            <div className="grid sm:grid-cols-2 gap-6">
              {volunteerOpportunities.map((opp) => {
                const Icon = iconMap[opp.icon] || Users;
                return (
                  <Card key={opp.id} className="border-0 shadow-sm hover:shadow-lg transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-teal-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2">{opp.title}</h3>
                      <p className="text-slate-600 text-sm mb-3">{opp.description}</p>
                      <p className="text-teal-600 text-xs font-medium">{opp.commitment}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Volunteer Form */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Become a Volunteer</CardTitle>
              </CardHeader>
              <CardContent>
                {volunteerSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Thank You!</h3>
                    <p className="text-slate-600">We'll be in touch soon about volunteer opportunities.</p>
                  </div>
                ) : (
                  <form onSubmit={handleVolunteerSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="vol-name">Name</Label>
                        <Input
                          id="vol-name"
                          value={volunteerForm.name}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="vol-email">Email</Label>
                        <Input
                          id="vol-email"
                          type="email"
                          value={volunteerForm.email}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="vol-phone">Phone</Label>
                      <Input
                        id="vol-phone"
                        type="tel"
                        value={volunteerForm.phone}
                        onChange={(e) => setVolunteerForm({ ...volunteerForm, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="vol-interest">Area of Interest</Label>
                      <Input
                        id="vol-interest"
                        placeholder="e.g., Mentoring, Workshop Facilitation"
                        value={volunteerForm.interest}
                        onChange={(e) => setVolunteerForm({ ...volunteerForm, interest: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="vol-message">Tell us about yourself</Label>
                      <Textarea
                        id="vol-message"
                        rows={4}
                        value={volunteerForm.message}
                        onChange={(e) => setVolunteerForm({ ...volunteerForm, message: e.target.value })}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={volunteerSubmitting} data-testid="volunteer-submit-btn">
                      {volunteerSubmitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                    {volunteerError && <p className="text-red-500 text-sm mt-2" data-testid="volunteer-error">{volunteerError}</p>}
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partner Section */}
      <section id="partner" className="py-20 lg:py-28 bg-gradient-to-b from-amber-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-teal-600 font-semibold text-sm tracking-wide uppercase">Partnership</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">Become a Corporate Partner</h2>
            <p className="text-slate-600">Join our network of forward-thinking organizations committed to creating opportunities and second chances.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Benefits */}
            <div className="bg-slate-50 rounded-3xl p-8 lg:p-12">
              <div className="flex items-center gap-3 mb-6">
                <Building className="w-8 h-8 text-teal-600" />
                <h3 className="text-xl font-bold text-slate-900">Partnership Benefits</h3>
              </div>
              <ul className="space-y-4">
                {[
                  'Access to pre-screened, job-ready candidates',
                  'Dedicated support for workplace integration',
                  'Work Opportunity Tax Credit eligibility',
                  'Community recognition and PR opportunities',
                  'Employee engagement volunteer programs',
                  'Regular progress reports and impact metrics',
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Partner Form */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Partner Inquiry</CardTitle>
              </CardHeader>
              <CardContent>
                {partnerSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Thank You!</h3>
                    <p className="text-slate-600">We'll reach out to discuss partnership opportunities.</p>
                  </div>
                ) : (
                  <form onSubmit={handlePartnerSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="partner-company">Company Name</Label>
                      <Input
                        id="partner-company"
                        value={partnerForm.company}
                        onChange={(e) => setPartnerForm({ ...partnerForm, company: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="partner-contact">Contact Name</Label>
                        <Input
                          id="partner-contact"
                          value={partnerForm.contact}
                          onChange={(e) => setPartnerForm({ ...partnerForm, contact: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="partner-email">Email</Label>
                        <Input
                          id="partner-email"
                          type="email"
                          value={partnerForm.email}
                          onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="partner-phone">Phone</Label>
                      <Input
                        id="partner-phone"
                        type="tel"
                        value={partnerForm.phone}
                        onChange={(e) => setPartnerForm({ ...partnerForm, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="partner-message">Tell us about your interest</Label>
                      <Textarea
                        id="partner-message"
                        rows={4}
                        value={partnerForm.message}
                        onChange={(e) => setPartnerForm({ ...partnerForm, message: e.target.value })}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={partnerSubmitting} data-testid="partner-submit-btn">
                      {partnerSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                    </Button>
                    {partnerError && <p className="text-red-500 text-sm mt-2" data-testid="partner-error">{partnerError}</p>}
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
